'use client';

import type { SetPrFileViewedResult } from '@/app/(chat)/api/chat/chat-tools/tools';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { completedSetViewedAutoApplyKeys } from './page-action-replays';
import { dispatchPrV2TabHandoff } from '@/lib/pr-v2/tab-handoff';
import { PR_V2_TABS } from '@/lib/pr-v2/tabs';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
const VIEWED_APPLY_RETRY_MAX_ATTEMPTS = 20;
const VIEWED_APPLY_RETRY_DELAY_MS = 100;
type SetPrFileViewedBlockProps = {
  args: SetPrFileViewedResult;
  disableAutoApply?: boolean;
  autoApplyKey?: string;
};
export function SetPrFileViewedBlock({
  args,
  disableAutoApply = false,
  autoApplyKey
}: SetPrFileViewedBlockProps) {
  const setFilesViewed = useDiffNavigationStore(state => state.setFilesViewed);
  const hasToolError = !args.success || args.filenames.length === 0;
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>(disableAutoApply ? hasToolError ? 'error' : 'success' : 'pending');
  const resolvedStatus = disableAutoApply ? hasToolError ? 'error' : 'success' : status;
  const stableFilenames = useMemo(() => args.filenames, [args.filenames.join('\u0000')]);
  useEffect(() => {
    if (disableAutoApply) {
      return;
    }
    if (hasToolError) {
      queueMicrotask(() => {
        setStatus('error');
      });
      return;
    }
    if (autoApplyKey && completedSetViewedAutoApplyKeys.has(autoApplyKey)) {
      queueMicrotask(() => {
        setStatus('success');
      });
      return;
    }
    dispatchPrV2TabHandoff(PR_V2_TABS.CHANGES);
    let cancelled = false;
    let attempts = 0;
    let timeoutId: number | null = null;
    const tryApply = () => {
      if (cancelled) {
        return;
      }
      if (setFilesViewed(stableFilenames, args.viewed)) {
        if (autoApplyKey) {
          completedSetViewedAutoApplyKeys.add(autoApplyKey);
        }
        setStatus('success');
        return;
      }
      attempts += 1;
      if (attempts >= VIEWED_APPLY_RETRY_MAX_ATTEMPTS) {
        setStatus('error');
        return;
      }
      timeoutId = window.setTimeout(tryApply, VIEWED_APPLY_RETRY_DELAY_MS);
    };
    timeoutId = window.setTimeout(tryApply, 0);
    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [stableFilenames, args.viewed, autoApplyKey, disableAutoApply, hasToolError, setFilesViewed]);
  const message = resolvedStatus === 'pending' ? 'Updating viewed state...' : resolvedStatus === 'success' ? args.summary : hasToolError ? args.summary || 'Could not update viewed state.' : 'Could not update viewed state.';
  return <div className="flex w-full items-center gap-2 overflow-hidden py-1 text-xs text-zinc-400">
      {resolvedStatus === 'pending' ? <Loader2 size={12} className="animate-spin" /> : null}
      <span>{message}</span>
    </div>;
}